import { bindPopper } from "./bindPopper";
import { maxWidthModifier } from "./modifiers/max-width";

function updateTooltip(element, title, disabled = false) {
  const tooltip = element._fbTooltip;
  if (!tooltip) return;

  const _title = ko.toJS(title)

  if (!_title) tooltip.remove();
  else {
    const text = tooltip.querySelector(".fc-popper__content");
    if (text) {
      text.innerHTML = _title;
      document.body.append(tooltip);
    }
  }

  if (disabled) {
    tooltip.style.opacity = 0;
  } else {
    tooltip.style.opacity = 1;
  }
}

function createTooltip(title, customClass) {
  let tooltip = document.createElement("div");
  tooltip.classList.add("fc-popper");
  tooltip.classList.add("fc-tooltip");

  if (customClass) tooltip.classList.add(customClass);

  tooltip.innerHTML = `<div class="fc-popper__container">
    <div class="fc-popper__content"></div>
  </div>
  <div class="fc-popper__arrow" data-popper-arrow></div>`;

  const text = tooltip.querySelector(".fc-popper__content");

  const _title = ko.toJS(title)

  if (_title) {
    text.innerHTML = _title;
    document.body.append(tooltip);
  }

  return {
    element: tooltip,
  };
}

function isOverflowing(element) {
  if (!element) return false;
  try {
    return (
      element.scrollWidth > element.clientWidth ||
      element.scrollHeight > element.clientHeight
    );
  } catch (e) {
    return false;
  }
}

export const binding = {
  init: function (
    element,
    valueAccessor,
    allBindings,
    viewModel,
    bindingContext
  ) {
    if (element._fbTooltip) {
      element._fbTooltip.remove();
      delete element._fbTooltip;
    }

    let title =
      allBindings.get("title") ||
      allBindings.get("tooltipText") ||
      element.getAttribute("title");

    element.removeAttribute("title");
    element._fbTooltipTitle = title;

    let options = valueAccessor() || {};
    const tooltipContentClass = allBindings.get('tooltipContentClass');
    const tooltipContentStyle = allBindings.get('tooltipContentStyle');
    const { placement, customClass, preventOverflow: enablePreventOverflow, maxWidth = 250, ...popperOptions } = options;
    const tooltipOnEllipsis = allBindings.get('tooltipOnEllipsis');

    let tooltip = createTooltip(title, customClass);

    // Apply custom class and styles to tooltip content element
    const contentEl = tooltip.element.querySelector('.fc-popper__content');
    if (contentEl) {
      if (tooltipContentClass) contentEl.classList.add(tooltipContentClass);
      if (tooltipContentStyle && typeof tooltipContentStyle === 'object') {
        Object.assign(contentEl.style, tooltipContentStyle);
      }
    }

    element._fbTooltip = tooltip.element;

    const modifiers = [
      { name: "arrow" },
      { name: "offset", options: { offset: [0, 10] } },
      { name: "flip" },
      maxWidthModifier({ width: maxWidth }),
    ];

    if (enablePreventOverflow) {
      modifiers.push({ name: "preventOverflow", options: { padding: { left: 8, right: 8 } } });
    }

    bindPopper(element, tooltip.element, {
      placement: placement || "top",
      modifiers,
    }).init({ hover: true, click: false, ...popperOptions });

    const recomputeDisabled = () => {
      const disabledByEllipsis = tooltipOnEllipsis ? !isOverflowing(element) : false;
      const externalDisabled = !!allBindings.get("fbPopperDisabled");
      const disabled = externalDisabled || disabledByEllipsis;
      updateTooltip(element, element._fbTooltipTitle, disabled);
    };

    recomputeDisabled();

    if (typeof ResizeObserver !== 'undefined' && tooltipOnEllipsis) {
      try {
        const ro = new ResizeObserver(() => recomputeDisabled());
        ro.observe(element);
        element.__fbPopperResizeObserver = ro;
      } catch (e) {}
    } else if (tooltipOnEllipsis) {
      const onResize = () => recomputeDisabled();
      window.addEventListener('resize', onResize);
      element.__fbPopperResizeFallback = onResize;
    }

    ko.utils.domNodeDisposal.addDisposeCallback(element, function () {
      if (element.__fbPopperResizeObserver) {
        try {
          element.__fbPopperResizeObserver.disconnect();
        } catch (e) {}
        delete element.__fbPopperResizeObserver;
      }
      if (element.__fbPopperResizeFallback) {
        window.removeEventListener('resize', element.__fbPopperResizeFallback);
        delete element.__fbPopperResizeFallback;
      }
      if (element._fbTooltip) {
        try {
          element._fbTooltip.remove();
        } catch (e) {}
        delete element._fbTooltip;
      }
    });
  },
  update: function (
    element,
    valueAccessor,
    allBindings,
    viewModel,
    bindingContext
  ) {
    let title =
      allBindings.get("title") ||
      allBindings.get("tooltipText") ||
      element.getAttribute("title");

    element._fbTooltipTitle = title;

    const tooltipOnEllipsis = allBindings.get('tooltipOnEllipsis');
    const disabledByEllipsis = tooltipOnEllipsis ? !isOverflowing(element) : false;
    const externalDisabled = !!allBindings.get("fbPopperDisabled");
    const disabled = externalDisabled || disabledByEllipsis;

    updateTooltip(element, title, disabled);
  },
};
